/**
 * Fixed Y-axis ranges for charts across the app.
 * Température: [-20 ; +60] °C
 * Consigne: [5 ; 30] °C
 * Humidité: [0 ; 100] %
 */

export const CHART_RANGES = {
  temperature: { min: -20, max: 60 },
  consigne: { min: 5, max: 30 },
  humidity: { min: 0, max: 100 },
} as const;

/** Plausible indoor temperature readings for outlier replacement (°C). */
export const INTERIOR_TEMPERATURE_BOUNDS = { min: 0, max: 38 } as const;

export type ChartMetricType = 'temperature' | 'consigne' | 'humidity';

/**
 * Returns fixed min/max for chart Y-axis based on metric type.
 * Returns null when the metric is not temp/consigne/humidity (caller uses data range).
 */
export function getChartYRange(
  title: string,
  unit: string | null | undefined,
  attributeTypeCode?: string | null
): { min: number; max: number } | null {
  const t = (title || '').toLowerCase();
  const u = (unit || '').trim().toLowerCase();
  const code = (attributeTypeCode || '').toUpperCase();

  // Consigne (setpoint) - check first to avoid matching "temp intérieur"
  if (
    t.includes('consigne') ||
    code.includes('SETPOINT') ||
    code.includes('TARGET_TEMPERATURE') ||
    code.includes('HEATING_SETPOINT')
  ) {
    return CHART_RANGES.consigne;
  }

  // Humidité
  if (t.includes('humidité') || u === '%' || code.includes('HUMIDITY')) {
    return CHART_RANGES.humidity;
  }

  // Température (intérieur, indoor)
  if (
    t.includes('intérieur') ||
    t.includes('temp. intérieur') ||
    t.includes('température') ||
    (u === '°c' && !t.includes('consigne')) ||
    code === 'INDOOR_TEMPERATURE' ||
    code === 'TEMPERATURE' ||
    code === 'TEMP'
  ) {
    return CHART_RANGES.temperature;
  }

  return null;
}

/**
 * Computes min, max, range for chart Y-axis and dataMin/dataMax for stats cards.
 * - Stats cards (Minimum/Maximum): always use dataMin/dataMax (actual values from data).
 * - Chart Y-axis: for temperature, starts at 0 unless data has negative values; otherwise uses fixed or data-based range.
 */
export function computeChartRange(
  dataValues: number[],
  title: string,
  unit: string | null | undefined,
  attributeTypeCode?: string | null
): { min: number; max: number; range: number; dataMin: number | null; dataMax: number | null } {
  const dataMin = dataValues.length > 0 ? Math.min(...dataValues) : null;
  const dataMax = dataValues.length > 0 ? Math.max(...dataValues) : null;

  const fixed = getChartYRange(title, unit, attributeTypeCode);
  if (fixed) {
    // Temperature: chart min = 0 unless data has negative values
    const isTemperature = fixed.min === CHART_RANGES.temperature.min && fixed.max === CHART_RANGES.temperature.max;
    if (isTemperature) {
      const chartMin = dataMin !== null && dataMin < 0 ? dataMin : 0;
      const chartMax = fixed.max;
      return {
        min: chartMin,
        max: chartMax,
        range: chartMax - chartMin,
        dataMin,
        dataMax,
      };
    }
    return {
      min: fixed.min,
      max: fixed.max,
      range: fixed.max - fixed.min,
      dataMin,
      dataMax,
    };
  }

  const min = dataMin ?? 0;
  const max = dataMax ?? 0;
  const range = max - min || 1;
  return { min, max, range, dataMin, dataMax };
}

function isInteriorTemperatureMetric(
  title: string,
  unit: string | null | undefined,
  attributeTypeCode?: string | null
): boolean {
  const range = getChartYRange(title, unit, attributeTypeCode);
  return (
    range !== null &&
    range.min === CHART_RANGES.temperature.min &&
    range.max === CHART_RANGES.temperature.max
  );
}

/**
 * Replaces implausible indoor temperature spikes/drops with the last valid reading.
 * Valid range: [0, 38] °C. The fallback value must also lie in that range.
 * Leading outliers without a prior valid point are returned as NaN (caller may filter).
 */
export function sanitizeStreamSeriesValues(
  values: number[],
  title: string,
  unit: string | null | undefined,
  attributeTypeCode?: string | null
): number[] {
  if (!isInteriorTemperatureMetric(title, unit, attributeTypeCode)) {
    return values.map((v) => sanitizeChartValue(v, title, unit, attributeTypeCode));
  }

  const { min, max } = INTERIOR_TEMPERATURE_BOUNDS;
  let lastValid: number | null = null;

  return values.map((raw) => {
    if (!Number.isFinite(raw)) {
      return raw;
    }
    if (raw >= min && raw <= max) {
      lastValid = raw;
      return raw;
    }
    if (lastValid !== null) {
      return lastValid;
    }
    return NaN;
  });
}

/**
 * Clamps a chart value when the metric has a fixed display range.
 * Returns the original value for metrics without a fixed range.
 */
export function sanitizeChartValue(
  value: number,
  title: string,
  unit: string | null | undefined,
  attributeTypeCode?: string | null
): number {
  const fixed = getChartYRange(title, unit, attributeTypeCode);
  if (!fixed || !Number.isFinite(value)) {
    return value;
  }

  return Math.max(fixed.min, Math.min(fixed.max, value));
}
